import { captureException, withScope } from '@sentry/nextjs'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/next'
import NextApp, { type AppProps } from 'next/app'
import { Inria_Serif, Open_Sans } from 'next/font/google'
import Head from 'next/head'
import Script from 'next/script'
import { Component, Fragment } from 'react'
import 'url-search-params-polyfill'

import {
  ModalLeaveBehindContextProvider,
  SortSelectContextProvider,
} from 'components/'
import { AnonymousIdProvider } from 'components/app/anonymousIdContext'
import { AttributionProvider } from 'components/app/AttributionContext'
import { ClientGeoLocationProvider } from 'components/app/clientGeoLocationContext'
import { ComparisonCartCardsProvider } from 'components/app/comparisonCartCardsContext'
import { ComparisonCartFromCategoryBannerCardsProvider } from 'components/app/comparisonCartFromCategoryBannerCardsContext'
import { GaSessionInfoProvider } from 'components/app/gaSessionInfoContext'
import { NavUpsellCardProvider } from 'components/app/navUpsellCardContext'
import { RecommendedCardProvider } from 'components/app/recommendedCardContext'
import { SegmentAnalyticsClientSideSetupTrigger } from 'components/app/segmentAnalyticsClientSideSetupTrigger'
import { StatsigProvider } from 'components/app/statsigProvider'
import { URL_OF_CUSTOM_PROXY_FOR_SEGMENT_CDN } from 'lib/constants'
import { usePageViewTracking } from 'utils/app/usePageViewTracking'
import { useBotDetectionAlerting } from 'utils/app/useBotDetectionAlerting'
import { checkErrorInstance } from 'utils/check-error-instance'
import { logger } from 'utils/logger'
import '../polyfills/from-entries-pollyfill'
import '../polyfills/promise-all-settled-polyfill'

import '../style.css'

const InriaSerif = Inria_Serif({
  weight: ['400', '700'],
  display: 'swap',
  style: ['normal'],
  variable: '--font-inria-serif',
  subsets: ['latin', 'latin-ext'],
})

const OpenSans = Open_Sans({
  weight: ['400', '600', '700'],
  style: ['normal'],
  display: 'swap',
  variable: '--font-open-sans',
  subsets: ['latin', 'latin-ext'],
})

const PRECONNECT_ORIGINS = [
  URL_OF_CUSTOM_PROXY_FOR_SEGMENT_CDN,
  'https://o1025464.ingest.sentry.io',
  'https://featuregates.org',
  'https://events.statsigapi.net',
] as const

const AppInner: React.FC<AppProps> = ({ Component, pageProps }) => {
  usePageViewTracking()
  useBotDetectionAlerting()

  return (
    <>
      <Head>
        <title>Compare Credit</title>
        {/* Global Site Tag (gtag.js) - Google Analytics */}
        <Script
          defer
          src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
          strategy="afterInteractive"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){
                  dataLayer.push(arguments);
                }
                gtag('js', new Date());
                gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}', {
                  page_path: window.location.pathname,
                });
              `,
          }}
        />
        {process.env.DEPLOYMENT_TARGET === 'production' && (
          <noscript
            dangerouslySetInnerHTML={{
              __html: `
                  <img src="https://sb.scorecardresearch.com/p?c1=2&c2=33669755&cv=2.0&cj=1" />
                `,
            }}
          />
        )}
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width"
          key="viewport"
        />
        {PRECONNECT_ORIGINS.map((origin) => (
          // https://developer.chrome.com/docs/lighthouse/performance/uses-rel-preconnect/
          <Fragment key={origin}>
            <link rel="preconnect" href={origin} />
            <link rel="dns-prefetch" href={origin} />
          </Fragment>
        ))}
      </Head>
      <SegmentAnalyticsClientSideSetupTrigger />

      {process.env.DEPLOYMENT_TARGET === 'production' && (
        <Script
          id="jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{"@context": "http://www.schema.org","@id": "https://www.comparecredit.com/#organization","@type": "Organization","name": "Compare Credit","url": "https://www.comparecredit.com/","sameAs": ["https://www.facebook.com/CompareCredit365/","https://www.linkedin.com/company/comparecredit365/about/","https://twitter.com/Compare__Credit"],"logo": "https://www.comparecredit.com/_next/image?url=%2Fstatic%2Flogo.svg&w=3840&q=75","description": "Compare the best offers on credit cards, personal loans, and more at CompareCredit.com. Find the best option for you and apply securely online today to start saving!"}`,
          }}
        />
      )}

      <GaSessionInfoProvider>
        <AnonymousIdProvider>
          <NavUpsellCardProvider>
            <RecommendedCardProvider>
              <AttributionProvider>
                <ComparisonCartCardsProvider>
                  <ComparisonCartFromCategoryBannerCardsProvider>
                    <ClientGeoLocationProvider>
                      <StatsigProvider>
                        <ModalLeaveBehindContextProvider>
                          <SortSelectContextProvider>
                            <main
                              className={`${InriaSerif.variable} ${OpenSans.variable} font-sans`}
                            >
                              <Component {...pageProps} />
                            </main>
                          </SortSelectContextProvider>
                        </ModalLeaveBehindContextProvider>
                      </StatsigProvider>
                    </ClientGeoLocationProvider>
                  </ComparisonCartFromCategoryBannerCardsProvider>
                </ComparisonCartCardsProvider>
              </AttributionProvider>
            </RecommendedCardProvider>
          </NavUpsellCardProvider>
        </AnonymousIdProvider>
      </GaSessionInfoProvider>
      <Analytics />
      <SpeedInsights />
    </>
  )
}

class AppErrorBoundary extends Component<React.PropsWithChildren> {
  componentDidCatch(
    originalError: unknown,
    { componentStack }: React.ErrorInfo,
  ): void {
    const error = checkErrorInstance(originalError)
    logger.debug(
      `Error caught by AppErrorBoundary; error (${typeof error}): `,
      error,
    )

    withScope((scope) => {
      scope.setExtra('componentStack', componentStack)
      captureException(error)
    })
  }

  render() {
    return this.props.children
  }
}

const AppWithErrorBoundary: React.FC<AppProps> &
  Partial<Pick<typeof NextApp, 'getInitialProps'>> = (props) => {
  return (
    <AppErrorBoundary>
      <AppInner {...props} />
    </AppErrorBoundary>
  )
}
AppWithErrorBoundary.getInitialProps = NextApp.getInitialProps
export default AppWithErrorBoundary
